import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { TrendingUp, Loader2 } from "lucide-react";

export default function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      const result = await register(email, password, name);
      if (result?.pending_approval) {
        setSuccess("Your registration has been received. Your account is awaiting administrator approval. You can sign in after your account is approved.");
        return;
      }
      nav("/dashboard");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-8 bg-slate-50">
      <form onSubmit={submit} className="w-full max-w-sm" data-testid="register-form">
        <div className="flex items-center gap-2 mb-8">
          <div className="w-8 h-8 bg-slate-900 flex items-center justify-center">
            <TrendingUp className="w-5 h-5 text-emerald-400" />
          </div>
          <span className="font-heading font-black text-lg tracking-tighter">KIRAN</span>
        </div>
        <h1 className="font-heading text-3xl font-black tracking-tighter mb-1">Create account</h1>
        <p className="text-sm text-slate-500 mb-8">Start building algo strategies in minutes.</p>

        {error && <div data-testid="register-error" className="mb-4 text-sm text-rose-600 bg-rose-50 border border-rose-200 px-3 py-2">{error}</div>}
        {success && <div data-testid="register-success" role="status" className="mb-4 text-sm text-green-700 bg-green-50 border border-green-200 px-3 py-2">{success}</div>}

        <label className="text-xs font-bold tracking-[0.15em] uppercase text-slate-500">Name</label>
        <input data-testid="register-name" value={name} onChange={(e) => setName(e.target.value)}
          className="mt-1 mb-4 w-full h-10 px-3 bg-white border border-slate-300 rounded-sm text-sm focus:outline-none focus:ring-1 focus:ring-blue-600" />

        <label className="text-xs font-bold tracking-[0.15em] uppercase text-slate-500">Email</label>
        <input data-testid="register-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
          className="mt-1 mb-4 w-full h-10 px-3 bg-white border border-slate-300 rounded-sm text-sm focus:outline-none focus:ring-1 focus:ring-blue-600" required />

        <label className="text-xs font-bold tracking-[0.15em] uppercase text-slate-500">Password</label>
        <input data-testid="register-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)}
          className="mt-1 mb-6 w-full h-10 px-3 bg-white border border-slate-300 rounded-sm text-sm focus:outline-none focus:ring-1 focus:ring-blue-600" required minLength={6} />

        <button data-testid="register-submit" disabled={busy}
          className="w-full h-10 bg-blue-600 text-white text-sm font-semibold rounded-sm hover:bg-blue-700 transition-colors active:scale-[0.98] flex items-center justify-center gap-2 disabled:opacity-60">
          {busy && <Loader2 className="w-4 h-4 animate-spin" />} Create account
        </button>

        <p className="text-sm text-slate-500 mt-6">
          Have an account? <Link to="/login" className="text-blue-600 font-semibold hover:underline" data-testid="go-login">Sign in</Link>
        </p>
      </form>
    </div>
  );
}
